import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  OutlinedInput,
} from "@mui/material";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import {
  getSwayyShipmentNumber,
  SwayyShipment,
  UpdateSwayyShipmentPayload,
} from "../../../../../models/SwayyShipment/SwayyShipment";
import { SwayyCarrier } from "../../../../../models/SwayyCarrier/SwayyCarrier";
import {
  canEditSwayyShipmentTracking,
  getSwayyShipmentNextStatuses,
  getSwayyShipmentStatusLabel,
} from "../../../../../models/SwayyShipment/SwayyShipmentFilters";
import { swayyColors, swayyFont, swayyRadii } from "../../../theme/tokens";

interface SwayyUpdateShipmentDialogProps {
  open: boolean;
  shipment: SwayyShipment | null;
  carriers: SwayyCarrier[];
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (payload: UpdateSwayyShipmentPayload) => void;
}

const inputSx = {
  borderRadius: swayyRadii.sm,
  fontFamily: swayyFont,
  fontSize: 13,
  bgcolor: swayyColors.white,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.border,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: swayyColors.pine,
  },
};

const toMysqlDatetime = (value: string): string => {
  if (!value) return "";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format("YYYY-MM-DD HH:mm:ss");
};

const SwayyUpdateShipmentDialog: React.FC<SwayyUpdateShipmentDialogProps> = ({
  open,
  shipment,
  carriers,
  loading = false,
  error = null,
  onClose,
  onSubmit,
}) => {
  const [statusId, setStatusId] = useState<string[]>([]);
  const [carrierId, setCarrierId] = useState<string[]>([]);
  const [awb, setAwb] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [expectedAt, setExpectedAt] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const nextStatuses = useMemo(
    () =>
      shipment ? getSwayyShipmentNextStatuses(String(shipment.status)) : [],
    [shipment],
  );

  const canEditTracking = shipment
    ? canEditSwayyShipmentTracking(String(shipment.status))
    : false;

  useEffect(() => {
    if (!open || !shipment) return;
    setStatusId([]);
    setCarrierId(
      shipment.carrier_id != null ? [String(shipment.carrier_id)] : [],
    );
    setAwb(shipment.awb || "");
    setTrackingUrl(shipment.tracking_url || "");
    setExpectedAt(
      shipment.expected_at
        ? dayjs(shipment.expected_at).format("YYYY-MM-DDTHH:mm")
        : "",
    );
    setLocalError(null);
  }, [open, shipment]);

  const carrierOptions = carriers.map((carrier) => ({
    id: String(carrier.id),
    value: carrier.name,
  }));

  const handleSubmit = () => {
    if (!shipment) return;
    setLocalError(null);

    const payload: UpdateSwayyShipmentPayload = {};
    if (statusId[0]) payload.status = statusId[0];

    if (canEditTracking) {
      if (carrierId[0]) payload.carrier_id = Number(carrierId[0]);
      else if (shipment.carrier_id != null && carrierId.length === 0) {
        // keep existing unless cleared intentionally — leave unset
      }
      if (awb.trim() !== (shipment.awb || "")) {
        payload.awb = awb.trim() || null;
      }
      if (trackingUrl.trim() !== (shipment.tracking_url || "")) {
        payload.tracking_url = trackingUrl.trim() || null;
      }
    }

    if (expectedAt) {
      const nextExpected = toMysqlDatetime(expectedAt);
      const currentExpected = shipment.expected_at
        ? dayjs(shipment.expected_at).format("YYYY-MM-DD HH:mm:ss")
        : "";
      if (nextExpected !== currentExpected) {
        payload.expected_at = nextExpected;
      }
    }

    if (Object.keys(payload).length === 0) {
      setLocalError("Change at least one field before saving.");
      return;
    }

    if (
      (payload.status === "picked_up" ||
        (shipment.status !== "created" &&
          payload.status &&
          payload.status !== "created")) &&
      !(payload.awb ?? shipment.awb) &&
      canEditTracking
    ) {
      // Backend enforces AWB for picked_up+
    }

    onSubmit(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: swayyRadii.xl,
          border: `1px solid ${swayyColors.border}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: swayyFont,
          fontWeight: 700,
          color: swayyColors.deepGreen,
        }}
      >
        Update shipment{shipment ? ` ${getSwayyShipmentNumber(shipment)}` : ""}
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <CustomTypography
          variant="body2"
          color={swayyColors.textSecondary}
          sx={{ fontFamily: swayyFont, mb: 2 }}
        >
          Current status:{" "}
          <strong>
            {shipment
              ? getSwayyShipmentStatusLabel(String(shipment.status))
              : "—"}
          </strong>
        </CustomTypography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <AppSearchDropdownMulti
            title={
              nextStatuses.length
                ? "Advance status"
                : "No further status transitions"
            }
            isMultiSelect={false}
            list={nextStatuses}
            value={nextStatuses.filter((option) => statusId.includes(option.id))}
            local
            fontSize="12px"
            disabled={loading || nextStatuses.length === 0}
            handleSelect={(items) => setStatusId(items.map((item) => item.id))}
          />

          {canEditTracking && (
            <>
              <AppSearchDropdownMulti
                title="Carrier"
                isMultiSelect={false}
                list={carrierOptions}
                value={carrierOptions.filter((option) =>
                  carrierId.includes(option.id),
                )}
                local
                floatingLabel
                fontSize="12px"
                borderColor={swayyColors.border}
                accentColor={swayyColors.pine}
                backgroundColor={swayyColors.white}
                disabled={loading}
                handleSelect={(items) =>
                  setCarrierId(items.map((item) => item.id))
                }
              />
              <OutlinedInput
                value={awb}
                onChange={(e) => setAwb(e.target.value)}
                placeholder="AWB"
                disabled={loading}
                fullWidth
                sx={{ ...inputSx, height: 42 }}
              />
              <OutlinedInput
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
                placeholder="Tracking URL"
                disabled={loading}
                fullWidth
                sx={{ ...inputSx, height: 42 }}
              />
            </>
          )}

          <OutlinedInput
            type="datetime-local"
            value={expectedAt}
            onChange={(e) => setExpectedAt(e.target.value)}
            disabled={loading}
            fullWidth
            sx={{ ...inputSx, height: 42 }}
          />
        </Box>

        {(localError || error) && (
          <CustomTypography
            variant="body2"
            color={swayyColors.danger}
            sx={{ fontFamily: swayyFont, mt: 1.5, whiteSpace: "pre-line" }}
          >
            {localError || error}
          </CustomTypography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <AppButton
          label="Cancel"
          onClick={onClose}
          disabled={loading}
          customStyles={{
            borderRadius: swayyRadii.md,
            bgcolor: "transparent",
            color: swayyColors.deepGreen,
            border: `1px solid ${swayyColors.border}`,
            "&:hover": { bgcolor: swayyColors.pineLight },
          }}
        />
        <AppButton
          label={loading ? "Saving..." : "Save changes"}
          onClick={handleSubmit}
          disabled={loading || !shipment}
          customStyles={{
            borderRadius: swayyRadii.md,
            bgcolor: swayyColors.pine,
            color: swayyColors.white,
            "&:hover": { bgcolor: swayyColors.pineHover },
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default observer(SwayyUpdateShipmentDialog);
